import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon,
  Calendar,
  Upload,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';
import englishService from '../services/englishService';
import RichTextEditor from '../components/RichTextEditor';

// Custom Alert Component
const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 9;

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    content: [''],
    AnyLink: '',
    date: new Date().toISOString().split('T')[0],
    author: '',
    tags: [],
    images: [],
    pdfReport: ''
  });
  const [pdfUploading, setPdfUploading] = useState(false);

  useEffect(() => {
    fetchNews(currentPage);
  }, [currentPage]);

  const fetchNews = async (page = 1) => {
    try {
      setLoading(true);
      const response = await englishService.news.getAll({ page, limit: itemsPerPage });
      
      let newsData;
      let pagination;
      
      if (response.data && Array.isArray(response.data)) {
        newsData = response.data;
        pagination = response.pagination;
      } else if (Array.isArray(response)) {
        newsData = response;
      } else {
        console.error('Unexpected response structure:', response);
        setError('Invalid data format');
        return;
      }
      
      setNews(newsData);
      
      if (pagination) {
        setTotalPages(pagination.totalPages);
        setTotalItems(pagination.totalItems);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to fetch news: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'content') {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData(prev => ({ ...prev, content: newContent }));
    } else if (name === 'tags') {
      const tagsArray = value.split(',').map(tag => tag.trim()).filter(Boolean);
      setFormData(prev => ({ ...prev, tags: tagsArray }));
    } else if (name === 'AnyLink') {
      setFormData(prev => ({ ...prev, AnyLink: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${type === 'thumbnail' ? 'Thumbnail' : 'Image'} size should be less than 5MB`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'thumbnail') {
          setFormData(prev => ({ ...prev, thumbnail: reader.result }));
        } else if (type === 'image') {
          if (formData.images.length >= 5) {
            setError('Maximum 5 images are allowed');
            return;
          }
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            const compressedImage = canvas.toDataURL('image/jpeg', 0.7);

            setFormData(prev => ({
              ...prev,
              images: [...prev.images, compressedImage]
            }));
          };
          img.src = reader.result;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }

    if (!formData.thumbnail) {
      setError('Thumbnail is required');
      return false;
    }

    if (!formData.content.some(paragraph => paragraph.trim())) {
      setError('At least one content paragraph is required');
      return false;
    }

    if (!formData.author) {
      setError('author is required');
      return false;
    }

    if (formData.images.length > 5) {
      setError('Maximum 5 images are allowed');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const formDataToSend = {
        title: formData.title.trim(),
        thumbnail: formData.thumbnail,
        content: formData.content.filter(paragraph => paragraph.trim() !== ''),
        AnyLink: formData.AnyLink || '',
        date: new Date(formData.date).toISOString(),
        author: formData.author.trim(),
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        images: formData.images || [],
        pdfReport: formData.pdfReport || ''
      };

      let response;
      if (selectedNews) {
        response = await englishService.news.update(selectedNews._id, formDataToSend);
      } else {
        response = await englishService.news.create(formDataToSend);
      }

      if (response.success) {
        setSuccess(selectedNews ? 'News updated successfully!' : 'News created successfully!');
        await fetchNews(currentPage);
        setShowModal(false);
        resetForm();
      } else {
        throw new Error(response.message || 'Failed to save news');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to save news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (newsItem) => {
    setSelectedNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      AnyLink: newsItem.AnyLink || '',
      thumbnail: newsItem.thumbnail,
      date: new Date(newsItem.date).toISOString().split('T')[0],
      author: newsItem.author,
      tags: newsItem.tags || [],
      images: newsItem.images || [],
      pdfReport: newsItem.pdfReport || ''
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.news.delete(selectedNews._id);
      setSuccess('News deleted successfully!');
      
      // If we deleted the last item on the page and it's not page 1, go back a page
      if (news.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchNews(currentPage);
      }
      
      setShowDeleteModal(false);
      setSelectedNews(null);
    } catch (error) {
      setError(error.message || 'Failed to delete news');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail: '',
      content: [''],
      AnyLink: '',
      date: new Date().toISOString().split('T')[0],
      author: '',
      tags: [],
      images: [],
      pdfReport: ''
    });
    setSelectedNews(null);
  };

  // Handle PDF upload
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('PDF size should be less than 50MB');
      return;
    }

    try {
      setPdfUploading(true);
      const formData = new FormData();
      formData.append('pdf', file);

      const response = await fetch(
        process.env.NODE_ENV === 'production' ? '/api/pdf/upload' : 'http://localhost:5500/api/pdf/upload',
        {
          method: 'POST',
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({ ...prev, pdfReport: result.data.path }));
        setSuccess('PDF uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(result.message || 'Failed to upload PDF');
      }
    } catch (error) {
      console.error('PDF upload error:', error);
      setError(error.message || 'Failed to upload PDF');
    } finally {
      setPdfUploading(false);
    }
  };

  // Handle PDF removal
  const handleRemovePdf = async () => {
    if (!formData.pdfReport) return;

    try {
      const response = await fetch(
        process.env.NODE_ENV === 'production' ? '/api/pdf/delete' : 'http://localhost:5500/api/pdf/delete',
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pdfPath: formData.pdfReport }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({ ...prev, pdfReport: '' }));
      }
    } catch (error) {
      console.error('PDF delete error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">News Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Total: {totalItems} news articles
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add News
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* News List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {news.map((newsItem) => (
          <div key={newsItem._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
            <div className="h-48 overflow-hidden">
              <img
                src={newsItem.thumbnail || '/api/placeholder/400/200'}
                alt={newsItem.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center mb-2">
                <span className="px-2 py-1 bg-sky-100 text-sky-600 text-sm rounded">
                  {newsItem.author}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{newsItem.title}</h3>
              <div className="space-y-2 text-gray-600 text-sm mb-4">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  {new Date(newsItem.date).toLocaleDateString()}
                </div>
                <div className="flex items-center">
                  <MessageSquare size={16} className="mr-2" />
                  {newsItem.comments?.length || 0} comments
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleEdit(newsItem)}
                  className="p-2 text-sky-500 hover:bg-sky-50 rounded-full transition-colors"
                >
                  <Edit2 size={20} />
                </button>
                <button
                  onClick={() => {
                    setSelectedNews(newsItem);
                    setShowDeleteModal(true);
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="flex space-x-1">
            {[...Array(totalPages)].map((_, index) => {
              const pageNum = index + 1;
              // Show first page, last page, current page, and pages around current
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 rounded-lg border ${
                      currentPage === pageNum
                        ? 'bg-sky-500 text-white border-sky-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (
                pageNum === currentPage - 2 ||
                pageNum === currentPage + 2
              ) {
                return <span key={pageNum} className="px-2">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedNews ? 'Edit News' : 'Add News'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* author */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    author *
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload thumbnail</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'thumbnail')}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                    onError={setError}
                  />
                </div>

                {/* Any Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Any Link
                  </label>
                  <input
                    type="text"
                    name="AnyLink"
                    value={formData.AnyLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags.join(', ')}
                    onChange={handleInputChange}
                    placeholder="Enter tags separated by commas"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Additional Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index)
                            }));
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'image')}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* PDF Report Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF Report (Optional)
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.pdfReport ? (
                      <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg flex-1">
                        <FileText className="text-green-600" size={24} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">PDF Uploaded</p>
                          <p className="text-xs text-green-600 truncate">{formData.pdfReport}</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemovePdf}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500 flex-1">
                        {pdfUploading ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                        ) : (
                          <>
                            <FileText className="text-gray-400 mb-2" size={32} />
                            <span className="text-sm text-gray-600">Upload PDF Report</span>
                            <span className="text-xs text-gray-400 mt-1">Max 50MB</span>
                          </>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept="application/pdf"
                          onChange={handlePdfUpload}
                          disabled={pdfUploading}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (selectedNews ? 'Update News' : 'Create News')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete News</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedNews.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !showModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
